import React, { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { Box, Typography, CircularProgress } from '@mui/material';

interface QrScannerProps {
  onScan: (data: string) => void;
  onError?: (error: string) => void;
}

export default function QrScanner({ onScan, onError }: QrScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    const reader = new BrowserMultiFormatReader();

    const startScanning = async () => {
      try {
        setIsLoading(true);
        setError('');

        // Get available video devices
        const videoInputDevices = await BrowserMultiFormatReader.listVideoInputDevices();

        if (videoInputDevices.length === 0) {
          throw new Error('No camera found');
        }

        // Use the first available camera (usually back camera on mobile)
        const selectedDeviceId = videoInputDevices[0].deviceId;

        // Get media stream
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: selectedDeviceId },
        });

        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }

        // Start decoding
        await reader.decodeFromVideoDevice(
          selectedDeviceId,
          videoRef.current!,
          (result, error) => {
            if (result) {
              onScan(result.getText());
            }
          }
        );

        setIsLoading(false);
      } catch (err: any) {
        console.error('Scanner error:', err);
        const errorMsg = err.message || 'Failed to access camera';
        setError(errorMsg);
        onError?.(errorMsg);
        setIsLoading(false);
      }
    };

    startScanning();

    // Cleanup
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, [onScan, onError]);

  return (
    <Box sx={{ position: 'relative', width: '100%', maxWidth: 500, mx: 'auto' }}>
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 2,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="error" variant="body2">
            {error}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Please allow camera access to scan QR codes
          </Typography>
        </Box>
      )}

      <video
        ref={videoRef}
        style={{
          width: '100%',
          maxWidth: 500,
          borderRadius: 8,
          display: error ? 'none' : 'block',
        }}
      />

      {!error && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 200,
            height: 200,
            border: '3px solid #0B6E4F',
            borderRadius: 2,
            pointerEvents: 'none',
            '&::before, &::after': {
              content: '""',
              position: 'absolute',
              width: 30,
              height: 30,
              border: '4px solid #B1D65D',
            },
            '&::before': {
              top: -3,
              left: -3,
              borderRight: 'none',
              borderBottom: 'none',
            },
            '&::after': {
              bottom: -3,
              right: -3,
              borderLeft: 'none',
              borderTop: 'none',
            },
          }}
        />
      )}

      <Typography
        variant="body2"
        sx={{ textAlign: 'center', mt: 2, color: 'text.secondary' }}
      >
        Position the QR code within the frame
      </Typography>
    </Box>
  );
}
